import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import Shell from "../Shell";
import { authHeaders } from "../api";
import "../dashboard.css";

/* ───────────────────────────── Utilities ───────────────────────────── */

function useDebounced(v, ms = 300) {
  const [s, setS] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setS(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return s;
}

const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/* ─────────────── Tiny inline calendar (DD/MM view, ISO value) ─────────────── */

function MiniDatePickerDMY({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || new Date();
  const [view, setView] = useState(
    new Date(selected.getFullYear(), selected.getMonth(), 1)
  );
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  return (
    <div ref={wrapRef} className="ni-date-wrap">
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        className="ni-input"
        placeholder="dd/mm/áááá"
      />

      {open && (
        <div className="ni-date-popover">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 8,
            }}
          >
            <button className="btn ghost sm" type="button" onClick={prevMonth}>
              ◀
            </button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>
              ▶
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 32px)",
              gap: 4,
              textAlign: "center",
              opacity: 0.7,
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 32px)",
              gap: 4,
              textAlign: "center",
            }}
          >
            {cells.map((c, i) => {
              const isOut = c === null || c.getMonth() !== view.getMonth();
              const active = c && isSameDay(c, selected);

              return (
                <button
                  key={i}
                  type="button"
                  disabled={!c}
                  onClick={() => {
                    if (!c) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: isOut ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: c ? "pointer" : "default",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Layout helper ───────────────────────────── */

function Row({ label, children }) {
  return (
    <div className="ni-row">
      <div className="ni-label">{label}</div>
      <div className="ni-field">{children}</div>
    </div>
  );
}

/* ───────────────────────────── Page component ───────────────────────────── */

export default function NewInterview() {
  const qs = new URLSearchParams(useLocation().search);
  const preselectUserId = qs.get("user");
  const memberInputRef = useRef(null);

  const [role, setRole] = useState("staff");
  const [myId, setMyId] = useState(null);
  const [myName, setMyName] = useState("");
  const isAdmin = role === "admin";

  const [userId, setUserId] = useState(preselectUserId || "");
  const [userQuery, setUserQuery] = useState("");
  const dUserQuery = useDebounced(userQuery, 300);
  const [userOptions, setUserOptions] = useState([]);

  const [interviewerId, setInterviewerId] = useState(null);
  const [interviewerOptions, setInterviewerOptions] = useState([]);

  const [dateIso, setDateIso] = useState(todayIso());

  const [kind, setKind] = useState("");
  const didNotAttend = kind === "no_show";
  const inHouse = kind === "in_house";
  const onPhone = kind === "remote";

  const [times, setTimes] = useState(1);
  const [flex, setFlex] = useState(false);
  const [peerSupport, setPeerSupport] = useState(false);
  const [rehabPlanning, setRehabPlanning] = useState(false);
  const [rehabInterview, setRehabInterview] = useState(false);
  const [urgentInterview, setUrgentInterview] = useState(false);
  const [familyInterview, setFamilyInterview] = useState(false);
  const [visit, setVisit] = useState(false);
  const [handleidsla, setHandleidsla] = useState(false);
  const [handleidslaN, setHandleidslaN] = useState(false);
  const [ororka, setOrorka] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", { headers: authHeaders() });
        setRole(data.role);
        setMyId(data.user_id);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!myId) return;
    setInterviewerId((prev) => prev ?? myId);

    if (!isAdmin) {
      (async () => {
        try {
          const { data } = await api.get(`/api/users/${myId}/summary/`, {
            headers: authHeaders(),
          });
          setMyName(data.full_name || "");
        } catch {}
      })();
    }
  }, [myId, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/api/users-list/", {
          params: { role: "staff", order_by: "name" },
          headers: authHeaders(),
        });
        setInterviewerOptions(Array.isArray(data) ? data : []);
        if (myId && !interviewerId) setInterviewerId(myId);
      } catch {}
    })();
  }, [isAdmin, myId, interviewerId]);

  useEffect(() => {
    if (!dUserQuery && !preselectUserId) {
      setUserOptions([]);
      return;
    }

    (async () => {
      try {
        const { data } = await api.get("/api/users-list/", {
          params: dUserQuery ? { q: dUserQuery, order_by: "name" } : {},
          headers: authHeaders(),
        });
        setUserOptions(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [dUserQuery, preselectUserId]);

  const preselectedUser = userOptions.find((u) => String(u.id) === String(userId));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert("Veldu félagsaðila.");
      return;
    }

    const effectiveInterviewer = isAdmin ? interviewerId : myId;
    if (String(userId) === String(effectiveInterviewer)) {
      alert("Viðtalandi og viðmælandi mega ekki vera sama manneskjan.");
      return;
    }

    const body = {
      user: Number(userId),
      ...(isAdmin && interviewerId ? { interviewer: Number(interviewerId) } : {}),
      interview_date: dateIso,
      did_not_attend: didNotAttend,
      in_house: inHouse,
      on_phone: onPhone,
      times,
      flex,
      peer_support: peerSupport,
      rehab_planning: rehabPlanning,
      rehab_interview: rehabInterview,
      urgent_interview: urgentInterview,
      family_interview: familyInterview,
      visit,
      handleidsla,
      handleidsla_nemar: handleidslaN,
      ororka,
    };

    try {
      await api.post("/api/interviews/", body, { headers: authHeaders() });
      alert("Viðtal skráð.");

      setUserId("");
      setUserQuery("");
      setUserOptions([]);
      setDateIso(todayIso());
      setKind("");
      setTimes(1);
      setFlex(false);
      setPeerSupport(false);
      setRehabPlanning(false);
      setRehabInterview(false);
      setUrgentInterview(false);
      setFamilyInterview(false);
      setVisit(false);
      setHandleidsla(false);
      setHandleidslaN(false);
      setOrorka(false);

      setTimeout(() => memberInputRef.current?.focus(), 0);
    } catch (e2) {
      alert(
        e2?.response?.data?.detail ||
          e2?.response?.data?.non_field_errors?.[0] ||
          "Tókst ekki að skrá viðtal."
      );
    }
  };

  return (
    <Shell title="Hugarafl starfsfólk" rightMenu>
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Skrá viðtal</h2>

      <form
        onSubmit={onSubmit}
        className="placeholder new-interview-form"
        style={{ maxWidth: 760 }}
      >
        <Row label="Nafn:">
          <div className="ni-relative">
            <input
              ref={memberInputRef}
              className="ni-input"
              placeholder="Leita að nafni / notendanafni / netfangi / síma"
              value={preselectedUser ? preselectedUser.full_name : userQuery}
              onChange={(e) => {
                setUserQuery(e.target.value);
                setUserId("");
              }}
              onFocus={() => {
                if (!userQuery && preselectedUser) setUserQuery(preselectedUser.full_name);
              }}
            />

            {userOptions.length > 0 && !userId && (
              <div className="ni-typeahead">
                {userOptions.map((u) => (
                  <div
                    key={u.id}
                    onMouseDown={() => {
                      setUserId(u.id);
                      setUserQuery(u.full_name);
                    }}
                    className="ni-typeahead-item"
                  >
                    {u.full_name} <span style={{ opacity: 0.6 }}>({u.username})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Row>

        <Row label="Viðtalandi:">
          {isAdmin ? (
            <select
              className="ni-input"
              value={interviewerId || ""}
              onChange={(e) => setInterviewerId(e.target.value || null)}
            >
              {interviewerOptions.length === 0 && <option value="">Hleð…</option>}
              {interviewerOptions.map((u) => (
                <option
                  key={u.id}
                  value={u.id}
                  disabled={String(u.id) === String(userId)}
                >
                  {u.full_name}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ opacity: 0.85, wordBreak: "break-word" }}>{myName || "—"}</div>
          )}
        </Row>

        <Row label="Tegund viðtals:">
          <div className="ni-inline-stack">
            <select
              className="ni-input"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">— Veldu —</option>
              <option value="in_house">Kom í hús</option>
              <option value="remote">Símaviðtal / Fjarfundur</option>
              <option value="no_show">Mætti ekki</option>
            </select>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Aðeins eitt má vera valið.</div>
          </div>
        </Row>

        <Row label="Dagsetning:">
          <MiniDatePickerDMY value={dateIso} onChange={setDateIso} />
        </Row>

        <Row label="Viðtal x 2:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={times === 2}
              onChange={(e) => setTimes(e.target.checked ? 2 : 1)}
            />
            Já
          </label>
        </Row>

        <Row label="Flex:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={flex}
              onChange={(e) => setFlex(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Jafningjastuðningur:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={peerSupport}
              onChange={(e) => setPeerSupport(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Endurhæfingaráætlun:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={rehabPlanning}
              onChange={(e) => setRehabPlanning(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Endurhæfingarviðtal:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={rehabInterview}
              onChange={(e) => setRehabInterview(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Bráðaviðtal:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={urgentInterview}
              onChange={(e) => setUrgentInterview(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Fjölskylduviðtal:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={familyInterview}
              onChange={(e) => setFamilyInterview(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Vitjun:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={visit}
              onChange={(e) => setVisit(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Handleiðsla:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={handleidsla}
              onChange={(e) => setHandleidsla(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Handleiðsla / nemar:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={handleidslaN}
              onChange={(e) => setHandleidslaN(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <Row label="Örorka:">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={ororka}
              onChange={(e) => setOrorka(e.target.checked)}
            />
            Já
          </label>
        </Row>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn" type="submit" disabled={!userId}>
            Skrá viðtal
          </button>
        </div>
      </form>
    </Shell>
  );
}